/**
 * ProjectsTable — the admin control room's "Projects" card (mockup
 * `#s-actrl` Projects card): local `<table>`, one row per factory project
 * with enrollment-state badges, a mini `PoolGauge`, the auto-advance gate,
 * and an "Open →" action into the project detail route (a later task).
 *
 * TODO(category): backend must expose project_type on factory Project to
 * scope admin views by category — until then `AdminControlRoom` passes
 * every registered project here regardless of `categoryKey` (verified
 * against backend: `Project` has no `project_type` field, only a free-form
 * `task` string that can't be reliably mapped without guessing).
 *
 * The "＋ Register project" button in the card header is wired to a stub
 * `onRegister` — the registration modal itself is Task 9.
 */

import type { Enrollment, PoolStatus, Project } from "../../../../types/factory";
import { PoolGauge, StatusBadge } from "../primitives";

export interface ProjectsTableProps {
  projects: Project[];
  poolByPid: Record<string, PoolStatus | undefined>;
  enrollmentsByPid: Record<string, Enrollment[] | undefined>;
  onOpen: (projectId: string) => void;
  onRegister: () => void;
  onSetVisible: (projectId: string, visible: boolean) => void;
  visibilityPendingPids: ReadonlySet<string>;
  visibilityErrorPids: ReadonlySet<string>;
}

function EnrollmentBadges({ enrollments }: { enrollments: Enrollment[] | undefined }) {
  const rows = enrollments ?? [];
  if (rows.length === 0) {
    return <StatusBadge tone="ghost">accepting · 0 enrolled</StatusBadge>;
  }
  const active = rows.filter((e) => e.state === "active").length;
  const pending = rows.filter((e) => e.state === "pending_approval").length;
  const blocked = rows.filter((e) => e.state === "blocked_pool_empty").length;
  return (
    <div className="flex flex-wrap gap-1.5">
      {active > 0 && <StatusBadge tone="sig">{active} active</StatusBadge>}
      {pending > 0 && <StatusBadge tone="sig">{pending} pending</StatusBadge>}
      {blocked > 0 && <StatusBadge tone="warn">{blocked} blocked</StatusBadge>}
    </div>
  );
}

function PoolCell({ pool }: { pool: PoolStatus | undefined }) {
  if (!pool) {
    return <span className="text-xs text-fg-faint">Pool pending…</span>;
  }
  const isEmpty = pool.available === 0;
  return (
    <div className="flex flex-col gap-1.5">
      <PoolGauge
        available={pool.available}
        awaitingSecond={pool.awaiting_second}
        gt={pool.gt}
        variant="mini"
      />
      <div className={`text-[11px] ${isEmpty ? "text-warn" : "text-fg-dim"}`}>
        <b className={isEmpty ? "text-warn" : "text-fg"}>{pool.available}</b> available
        {isEmpty ? " · pool empty" : ` · ${pool.awaiting_second} awaiting 2nd`}
      </div>
    </div>
  );
}

export function ProjectsTable({
  projects,
  poolByPid,
  enrollmentsByPid,
  onOpen,
  onRegister,
  onSetVisible,
  visibilityPendingPids,
  visibilityErrorPids,
}: ProjectsTableProps) {
  return (
    <div className="rounded border border-border/50 bg-surface">
      <div className="flex items-center gap-3 border-b border-border/50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-fg">Projects</h3>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onRegister}
          className="rounded border border-border-strong px-3 py-1.5 text-xs font-semibold text-fg transition hover:bg-white/[0.04]"
        >
          ＋ Register project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="p-8 text-center text-sm text-fg-muted">No projects registered yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-border/50 text-[11px] uppercase tracking-wide text-fg-dim">
                <th className="px-5 py-2.5 font-medium">Project</th>
                <th className="px-5 py-2.5 font-medium">Enrollments</th>
                <th className="w-[230px] px-5 py-2.5 font-medium">Pool</th>
                <th className="px-5 py-2.5 font-medium">Auto-advance</th>
                <th className="px-5 py-2.5 font-medium">Visible to annotators</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.project_id} className="border-b border-border/50 last:border-b-0">
                  <td className="px-5 py-3.5 align-top">
                    <div className="text-sm font-medium text-fg">{project.name}</div>
                    <div className="text-xs text-fg-muted">
                      {project.project_id} · {project.task}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <EnrollmentBadges enrollments={enrollmentsByPid[project.project_id]} />
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <PoolCell pool={poolByPid[project.project_id]} />
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <StatusBadge tone="ghost">
                      from phase {project.gate_policy.auto_advance_from_phase}
                    </StatusBadge>
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={project.hidden !== true}
                      aria-label={`${project.name} visible to annotators`}
                      disabled={visibilityPendingPids.has(project.project_id)}
                      onClick={() => onSetVisible(project.project_id, project.hidden === true)}
                      className={`inline-flex min-w-[92px] items-center gap-2 rounded border px-2.5 py-1.5 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${
                        project.hidden === true
                          ? "border-border-strong bg-white/[0.02] text-fg-muted hover:bg-white/[0.05]"
                          : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`h-2 w-2 rounded-sharp ${
                          project.hidden === true ? "bg-fg-faint" : "bg-primary"
                        }`}
                      />
                      {visibilityPendingPids.has(project.project_id)
                        ? "Saving…"
                        : project.hidden === true
                          ? "Hidden"
                          : "Visible"}
                    </button>
                    {visibilityErrorPids.has(project.project_id) && (
                      <div role="alert" className="mt-1.5 text-[11px] text-warn">
                        Could not update visibility
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right align-top">
                    <button
                      type="button"
                      onClick={() => onOpen(project.project_id)}
                      className="text-xs font-semibold text-primary transition hover:text-primary/80"
                    >
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProjectsTable;
